import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';

/**
 * PeriVerse Themed Button Component
 *
 * A customized button component that follows the PeriVerse design system.
 * Extends Material-UI Button with additional styling and variant options.
 */

// Styled button with custom variants
const StyledButton = styled(MuiButton)(({ theme, size }) => ({
  boxShadow: '0 0 0 0 rgba(0, 0, 0, 0.25)',
  '&:hover': {
    boxShadow: '0 0 0 0 rgba(0, 0, 0, 0.25)',
  },
  ...(size !== 'small' && {
    ...theme.typography.buttonLarge,
    borderRadius: '8px',
    '&&&': {
      padding: '8px 22px',
      lineHeight: '26px',
    },
  }),
}));

export interface ButtonProps extends Omit<MuiButtonProps, 'variant'> {
  /**
   * The variant of the button
   * @default 'contained'
   */
  variant?: 'contained' | 'outlined' | 'text';
  /**
   * The color of the button
   * @default 'primary'
   */
  color?:
    | 'primary'
    | 'secondary'
    | 'error'
    | 'warning'
    | 'info'
    | 'success'
    | 'inherit';
  /**
   * The size of the button
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Whether the button is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Whether the button is in loading state
   * @default false
   */
  loading?: boolean;
  /**
   * The content of the button
   */
  children: React.ReactNode;
  /**
   * Click handler
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * PeriVerse Button Component
 *
 * A themed button component that provides consistent styling across the application.
 * Supports all Material-UI Button props with additional PeriVerse-specific styling.
 *
 * @example
 * ```tsx
 * <Button variant="contained" color="primary" onClick={handleClick}>
 *   Click me
 * </Button>
 *
 * <Button variant="outlined" color="secondary" size="small">
 *   Small button
 * </Button>
 *
 * <Button variant="text" color="error" disabled>
 *   Disabled button
 * </Button>
 * ```
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  children,
  onClick,
  ...props
}) => {
  return (
    <StyledButton
      variant={variant}
      color={color}
      size={size}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {loading ? 'Loading...' : children}
    </StyledButton>
  );
};

export default Button;
